import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { getVisitorConsentAttachmentPreviewKind } from "./visitor-consent-attachment-preview.util";
import { VisitorConsentPdfAttachmentPreview } from "./visitor-consent-pdf-attachment-preview.component";

type AttachmentPreviewTestIds = {
  root?: string;
  image?: string;
  pdf?: string;
  link?: string;
  unavailable?: string;
  loading?: string;
};

type P = {
  fileViewerUrl: string;
  fileName: string | null;
  openLinkLabel: string;
  pdfPreviewUnavailableMessage: string;
  pdfPreviewLoadingMessage: string;
  pdfPreviewHeight?: number | string;
  linkButtonSx?: SxProps<Theme>;
  testIds?: AttachmentPreviewTestIds;
};

type ImagePreviewProps = {
  fileViewerUrl: string;
  displayName: string;
  openLinkLabel: string;
  previewUnavailableMessage: string;
  linkButtonSx?: SxProps<Theme>;
  testIds?: AttachmentPreviewTestIds;
};

function VisitorConsentImageAttachmentPreview({
  fileViewerUrl,
  displayName,
  openLinkLabel,
  previewUnavailableMessage,
  linkButtonSx,
  testIds
}: Readonly<ImagePreviewProps>) {
  const [imageFailed, setImageFailed] = useState(false);

  if (imageFailed) {
    return (
      <Stack
        spacing={1.5}
        data-testid={testIds?.unavailable ?? "visitor-consent-attachment-preview-unavailable"}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          bgcolor: "grey.50"
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {previewUnavailableMessage}
        </Typography>
        <Button
          component="a"
          href={fileViewerUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          sx={{ alignSelf: "flex-start", ...linkButtonSx }}
          data-testid={testIds?.link ?? "visitor-consent-view-attachment"}
        >
          {openLinkLabel}
        </Button>
      </Stack>
    );
  }

  return (
    <Box
      data-testid={testIds?.root ?? "visitor-consent-attachment-preview"}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        width: "100%",
        py: 1,
        bgcolor: "grey.50"
      }}
    >
      <Box
        component="img"
        src={fileViewerUrl}
        alt={displayName}
        onError={() => setImageFailed(true)}
        data-testid={testIds?.image ?? "visitor-consent-attachment-preview-image"}
        sx={{
          display: "block",
          width: "100%",
          height: "auto"
        }}
      />
    </Box>
  );
}

export function VisitorConsentDocumentAttachmentPreview({
  fileViewerUrl,
  fileName,
  openLinkLabel,
  pdfPreviewUnavailableMessage,
  pdfPreviewLoadingMessage,
  pdfPreviewHeight = 320,
  linkButtonSx,
  testIds
}: Readonly<P>) {
  const kind = getVisitorConsentAttachmentPreviewKind(fileName);
  const displayName = fileName?.trim() || openLinkLabel;

  if (kind === "image") {
    return (
      <VisitorConsentImageAttachmentPreview
        fileViewerUrl={fileViewerUrl}
        displayName={displayName}
        openLinkLabel={openLinkLabel}
        previewUnavailableMessage={pdfPreviewUnavailableMessage}
        linkButtonSx={linkButtonSx}
        testIds={testIds}
      />
    );
  }

  if (kind === "pdf") {
    return (
      <VisitorConsentPdfAttachmentPreview
        fileViewerUrl={fileViewerUrl}
        fileName={displayName}
        openLinkLabel={openLinkLabel}
        pdfPreviewUnavailableMessage={pdfPreviewUnavailableMessage}
        pdfPreviewLoadingMessage={pdfPreviewLoadingMessage}
        pdfPreviewHeight={pdfPreviewHeight}
        linkButtonSx={linkButtonSx}
        testIds={testIds}
      />
    );
  }

  return (
    <Button
      component="a"
      href={fileViewerUrl}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      size="small"
      sx={{ alignSelf: "flex-start", ...linkButtonSx }}
      data-testid={testIds?.link ?? "visitor-consent-view-attachment"}
    >
      {openLinkLabel}
    </Button>
  );
}
